import {announce} from '@github-ui/aria-live'
import {useAnalytics} from '@github-ui/use-analytics'
import {useActiveRoute} from '@github-ui/pull-requests/hooks/use-active-route'
import {Button, CounterLabel, Label, Spinner, Tooltip} from '@primer/react'
import {AutomatedReviewDiffEntry} from './AutomatedReviewDiffEntry'
import React, {useState, useRef, useCallback, useMemo} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {ApplySuggestionDialog} from '../CodeSuggestionActions/ApplySuggestionDialog'
import {useApplyAutomatedSuggestionMutation} from '@github-ui/pull-requests/use-apply-automated-suggestion-mutation'
import type {AutomatedReviewSuggestionProps} from './AutomatedReviewSuggestion'
import {AgentIcon, FoldIcon, UnfoldIcon} from '@primer/octicons-react'
import type {Suggestion, SuggestedChangeLineRange} from '../../../conversation-types'
import styles from './AutomatedReviewSuggestionStates.module.css'
import {CopilotFixDialog} from '@github-ui/copilot-apply-changes-button/CopilotFixDialog'
import {SplitButton, type SplitButtonItem} from '@github-ui/copilot-fix-controls/SplitButton'
import {useFixWithCopilot} from '@github-ui/copilot-apply-changes-button/useFixWithCopilot'
import {useOpenManageBatchPanel} from '@github-ui/pull-requests/use-open-manage-batch-panel'
import {
  useSuggestionInBatch,
  useSuggestionBatchActions,
  useIsSuggestionBatchPending,
  useSuggestionBatch,
  usePullRequestBasePath,
  useComparisonInfo,
  useIsInUnifiedBatch,
  useUnifiedBatchActions,
} from '@github-ui/pull-requests/pull-request-store-selectors'
import {saveActiveBatchToStorage} from '@github-ui/pull-requests/helpers/suggestion-batch-storage'
import {
  getLineRangesFromDiffEntries,
  validateSuggestedChangeCanBeAddedToBatch,
} from '../../../helpers/suggested-changes'
import {CodeSuggestionUnavailable} from '../CodeSuggestionActions/CodeSuggestionUnavailable'
import {
  UnifiedCommentActionTelemetryEvents,
  UnifiedCommentActionTelemetryPlacements,
  useUnifiedCommentActionTelemetry,
} from '../unified-comment-action-telemetry'

const SUGGESTION_FOLD_LINE_THRESHOLD = 20

function getTotalDiffLineCount(suggestion: Suggestion | undefined): number {
  if (!suggestion?.diffEntries) return 0
  return suggestion.diffEntries.reduce((total, entry) => total + (entry.diffLines?.length ?? 0), 0)
}

interface AutomatedReviewSuggestionBaseProps {
  suggestion: Suggestion
  actions?: React.ReactNode
  initialDiffCollapsed?: boolean
}

function AutomatedReviewSuggestionBase({
  suggestion,
  actions,
  initialDiffCollapsed = false,
}: AutomatedReviewSuggestionBaseProps) {
  const diffEntries = suggestion.diffEntries || []
  const suggestedChangesCount = diffEntries.length || 0

  return (
    <div
      className={`d-flex flex-column color-fg-default ${styles.suggestion}`}
      data-testid="automated-review-suggestion"
    >
      {/* Metadata */}
      {suggestedChangesCount > 0 && (
        <div className="d-flex flex-items-center flex-justify-between text-small color-fg-muted pt-1">
          <div>
            Suggested changeset <CounterLabel scheme="secondary">{suggestedChangesCount}</CounterLabel>
          </div>
        </div>
      )}

      {/* Diffs */}
      {diffEntries.map(diffEntry => {
        return (
          <AutomatedReviewDiffEntry
            key={diffEntry.path}
            diffEntry={diffEntry}
            initialCollapsed={initialDiffCollapsed}
          />
        )
      })}

      {actions}
    </div>
  )
}

export function AutomatedReviewSuggestionPending(_props: AutomatedReviewSuggestionProps) {
  return (
    <div className={styles.suggestionPending} data-testid="automated-review-suggestion-pending">
      <Spinner size="medium" />
      Loading suggestion...
    </div>
  )
}

// TODO(@aliceclv): we store the suggestion_error_message in the DB, we can display it here
export function AutomatedReviewSuggestionError(_props: AutomatedReviewSuggestionProps) {
  return (
    <div className={styles.suggestionError} data-testid="automated-review-suggestion-error">
      <h3 className={styles.suggestionErrorTitle}>Sorry, we could not generate an autofix suggestion.</h3>
      <p className="color-fg-muted">
        Try pushing a new commit or if the problem persists{' '}
        <a className="fgColor-link" href="https://support.github.com/">
          <u>contact support</u>
        </a>
        .
      </p>
    </div>
  )
}

export function AutomatedReviewSuggestionOutdated({suggestion, isClassicExperience}: AutomatedReviewSuggestionProps) {
  const Actions = (
    <div className="d-flex flex-items-center flex-justify-between gap-1">
      <span className="color-fg-muted text-small">Unable to commit as this autofix suggestion is now outdated.</span>

      {isClassicExperience ? (
        <Button size="small" variant="primary" disabled>
          Commit suggestion
        </Button>
      ) : (
        <Tooltip text="This autofix suggestion is outdated">
          <Button variant="default" inactive>
            Apply suggestion
          </Button>
        </Tooltip>
      )}
    </div>
  )
  return suggestion && <AutomatedReviewSuggestionBase suggestion={suggestion} actions={Actions} />
}

export function AutomatedReviewSuggestionApplied({suggestion}: AutomatedReviewSuggestionProps) {
  const [isFolded, setIsFolded] = useState(true)
  const fold = () => setIsFolded(prev => !prev)

  return (
    <div className={styles.suggestionApplied}>
      <div className="d-flex flex-justify-between flex-items-center">
        <span className={styles.suggestionAppliedText}>This autofix suggestion was applied.</span>
        <Button
          variant="invisible"
          onClick={fold}
          leadingVisual={isFolded ? UnfoldIcon : FoldIcon}
          size="small"
          className="color-fg-muted"
        >
          {isFolded ? 'Show' : 'Hide'} autofix suggestion
        </Button>
      </div>
      {!isFolded && suggestion && <AutomatedReviewSuggestionBase suggestion={suggestion} />}
    </div>
  )
}

export function AutomatedReviewSuggestionPresent({
  automatedCommentId,
  basePath,
  foldLongSuggestion,
  commentAuthorLogin,
  commentDatabaseId,
  commentTitle,
  fixWithCopilotProps,
  isClassicExperience,
  suggestion,
  threadId,
}: AutomatedReviewSuggestionProps) {
  const shouldStartCollapsed = foldLongSuggestion && getTotalDiffLineCount(suggestion) > SUGGESTION_FOLD_LINE_THRESHOLD
  const [isApplySuggestionDialogOpen, setIsApplySuggestionDialogOpen] = useState(false)
  const openDialog = useCallback(() => setIsApplySuggestionDialogOpen(true), [])
  const closeDialog = useCallback(() => setIsApplySuggestionDialogOpen(false), [])

  const diffEntries = suggestion?.diffEntries || []
  const suggestedChangesCount = diffEntries.length || 0
  const applyButtonRef = useRef<HTMLButtonElement>(null)
  // Note(@aliceclv): this is optimized for Universe ship & code quality
  // We should have a default commit message passed from the backend when adapting the component to code scanning & CCR
  const defaultCommitMessage = `Potential fix for pull request finding${commentTitle ? ` '${commentTitle}'` : ''}`
  const {mutate: applyAutomatedSuggestion} = useApplyAutomatedSuggestionMutation(automatedCommentId, basePath)
  const unifiedBatchPrCommentsEnabled = isFeatureEnabled('unified_batch_pr_comments') && !isClassicExperience

  const handleCommitSuggestion = useCallback(
    (
      commitMessage: string,
      onError: (error: Error, type?: string, friendlyMessage?: string) => void,
      onCompleted: () => void,
    ) => {
      applyAutomatedSuggestion(
        {message: commitMessage},
        {
          onSuccess: () => {
            closeDialog()
            onCompleted()
          },
          onError: (error: Error) => {
            onError(error, 'applyAutomatedSuggestion', 'Failed to commit suggested changes')
          },
        },
      )
    },
    [applyAutomatedSuggestion, closeDialog],
  )

  if (!suggestion) return null

  const actions = (
    <>
      {unifiedBatchPrCommentsEnabled ? (
        <CommitSuggestionButton
          applyButtonRef={applyButtonRef}
          onApplyButtonClick={openDialog}
          commentGlobalId={automatedCommentId}
          commentDatabaseId={commentDatabaseId ?? undefined}
          threadId={threadId}
          suggestion={suggestion}
        />
      ) : isClassicExperience ? (
        <RailsExperienceButtons
          applyButtonRef={applyButtonRef}
          onApplyButtonClick={openDialog}
          fixWithCopilotProps={fixWithCopilotProps}
          basePath={basePath}
        />
      ) : (
        // commentDatabaseId shouldn't ever be null here, but we add this check to satisfy TypeScript
        commentDatabaseId != null && (
          <ReactExperienceButtons
            commentDatabaseId={commentDatabaseId}
            applyButtonRef={applyButtonRef}
            onApplyButtonClick={openDialog}
            suggestion={suggestion}
            fixWithCopilotProps={fixWithCopilotProps}
            basePath={basePath}
          />
        )
      )}

      {isApplySuggestionDialogOpen && suggestedChangesCount > 0 && (
        <ApplySuggestionDialog
          authorLogins={commentAuthorLogin ? [commentAuthorLogin] : []}
          batchSize={suggestedChangesCount}
          commentKind="ccr"
          defaultCommitMessage={defaultCommitMessage}
          onClose={closeDialog}
          onCommit={handleCommitSuggestion}
          dialogTitle={
            unifiedBatchPrCommentsEnabled
              ? 'Commit suggestions'
              : isClassicExperience
                ? 'Commit suggestion'
                : 'Apply suggestion'
          }
          returnFocusRef={applyButtonRef}
          submitButtonLabel={unifiedBatchPrCommentsEnabled ? 'Commit suggestions' : undefined}
        />
      )}
    </>
  )

  return (
    <AutomatedReviewSuggestionBase
      suggestion={suggestion}
      actions={actions}
      initialDiffCollapsed={shouldStartCollapsed}
    />
  )
}

type SuggestionActionMode = 'commit' | 'fix_with_copilot'

const RAILS_SPLIT_ITEMS: Array<SplitButtonItem<SuggestionActionMode>> = [
  {
    value: 'commit',
    label: 'Commit suggestion',
    description: 'Commit this suggested change directly.',
  },
  {
    value: 'fix_with_copilot',
    label: 'Fix with Copilot',
    description: 'Revise this suggestion with Copilot.',
  },
]

const REACT_SPLIT_ITEMS: Array<SplitButtonItem<SuggestionActionMode>> = [
  {
    value: 'commit',
    label: 'Apply suggestion',
    description: 'Apply this suggested change directly.',
  },
  {
    value: 'fix_with_copilot',
    label: 'Fix with Copilot',
    description: 'Revise this suggestion with Copilot.',
  },
]

function ViewAgentSessionButton({taskUrl}: {taskUrl: string}) {
  return (
    <div className="d-flex gap-2 flex-items-center flex-justify-end flex-wrap">
      <Button size="small" variant="default" as="a" href={taskUrl} leadingVisual={AgentIcon} className="no-underline">
        View agent session
      </Button>
    </div>
  )
}

type CommitSuggestionButtonProps = {
  applyButtonRef: React.RefObject<HTMLButtonElement | null>
  onApplyButtonClick: () => void
  commentGlobalId: string
  commentDatabaseId?: number | null
  threadId?: string
  suggestion: Suggestion
}

type SuggestionBatchActionsProps = Omit<CommitSuggestionButtonProps, 'commentDatabaseId'> & {
  commentDatabaseId: number
}

function CommitSuggestionButton({
  applyButtonRef,
  onApplyButtonClick,
  commentGlobalId,
  commentDatabaseId,
  threadId,
  suggestion,
}: CommitSuggestionButtonProps) {
  const sendTelemetry = useUnifiedCommentActionTelemetry(
    {
      hasSuggestion: true,
      isOutdated: false,
      kind: 'ccr-with-suggestion',
      suggestionIndex: undefined,
    },
    {
      controlPlacement: UnifiedCommentActionTelemetryPlacements.perSuggestionControls,
    },
  )
  const handleCommitSuggestionsClick = useCallback(() => {
    sendTelemetry(UnifiedCommentActionTelemetryEvents.commitSuggestionsClicked)
    onApplyButtonClick()
  }, [onApplyButtonClick, sendTelemetry])

  if (commentDatabaseId == null) {
    return (
      <div className="d-flex gap-2 flex-items-center flex-justify-end flex-wrap">
        <Button
          data-testid="commit-suggestion-button"
          variant="primary"
          ref={applyButtonRef}
          onClick={handleCommitSuggestionsClick}
        >
          Commit suggestions
        </Button>
      </div>
    )
  }

  return (
    <SuggestionBatchActions
      applyButtonRef={applyButtonRef}
      onApplyButtonClick={handleCommitSuggestionsClick}
      commentGlobalId={commentGlobalId}
      commentDatabaseId={commentDatabaseId}
      threadId={threadId}
      suggestion={suggestion}
    />
  )
}

function SuggestionBatchActions({
  applyButtonRef,
  onApplyButtonClick,
  commentGlobalId,
  commentDatabaseId,
  threadId,
  suggestion,
}: SuggestionBatchActionsProps) {
  const batchId = useMemo(() => ({commentDatabaseId, suggestionIndex: undefined}), [commentDatabaseId])
  const isInBatch = useIsInUnifiedBatch(batchId)
  const {addItem, removeItem} = useUnifiedBatchActions()
  const openManageBatchPanel = useOpenManageBatchPanel()
  const addToBatchButtonRef = useRef<HTMLButtonElement>(null)
  const pathLineRanges = useMemo(
    () => getLineRangesFromDiffEntries(suggestion.diffEntries || []),
    [suggestion.diffEntries],
  )

  const batchItem = useMemo(
    () => ({
      commentGlobalId,
      commentDatabaseId,
      path: pathLineRanges[0]?.path ?? '',
      pathLineRanges: pathLineRanges.length > 0 ? pathLineRanges : undefined,
      isOutdated: false,
      threadId: threadId ? Number(threadId) : null,
      kind: 'ccr-with-suggestion' as const,
      hasSuggestion: true as const,
      suggestionIndex: undefined,
      lineRange: pathLineRanges[0]?.lineRange,
    }),
    [commentGlobalId, commentDatabaseId, pathLineRanges, threadId],
  )
  const sendTelemetry = useUnifiedCommentActionTelemetry(batchItem, {
    controlPlacement: UnifiedCommentActionTelemetryPlacements.perSuggestionControls,
  })

  const [addToBatchError, setAddToBatchError] = useState<string | undefined>()

  const handleAddToBatch = useCallback(() => {
    sendTelemetry(UnifiedCommentActionTelemetryEvents.addToBatchClicked)
    const result = addItem(batchItem)
    if (!result.ok) {
      setAddToBatchError(result.reason)
      announce(result.reason)
    } else {
      setAddToBatchError(undefined)
      openManageBatchPanel({returnFocusTo: addToBatchButtonRef.current})
    }
  }, [addItem, batchItem, openManageBatchPanel, sendTelemetry])

  const handleRemoveFromBatch = useCallback(() => {
    sendTelemetry(UnifiedCommentActionTelemetryEvents.removeFromBatchClicked)
    removeItem(batchId)
    setAddToBatchError(undefined)
  }, [removeItem, batchId, sendTelemetry])

  return (
    <>
      <div className="d-flex gap-2 flex-items-center flex-justify-end flex-wrap">
        <Button
          ref={addToBatchButtonRef}
          variant={isInBatch ? 'danger' : 'default'}
          onClick={isInBatch ? handleRemoveFromBatch : handleAddToBatch}
        >
          {isInBatch ? 'Remove from batch' : 'Add to batch'}
        </Button>
        {!isInBatch && (
          <Button
            data-testid="commit-suggestion-button"
            variant="primary"
            ref={applyButtonRef}
            onClick={onApplyButtonClick}
          >
            Commit suggestions
          </Button>
        )}
      </div>
      {addToBatchError && <CodeSuggestionUnavailable reason={addToBatchError} className="m-0" />}
    </>
  )
}

function RailsExperienceButtons({
  applyButtonRef,
  onApplyButtonClick,
  fixWithCopilotProps,
  basePath,
}: {
  applyButtonRef: React.RefObject<HTMLButtonElement | null>
  onApplyButtonClick: () => void
  fixWithCopilotProps?: AutomatedReviewSuggestionProps['fixWithCopilotProps']
  basePath: string
}) {
  const showBatchButton = isFeatureEnabled('prx_files')
  const {
    actionMode,
    setActionMode,
    taskUrl,
    isFixWithCopilotOpen,
    setIsFixWithCopilotOpen,
    handleMainButtonClick,
    handleFixSubmit,
  } = useFixWithCopilotActions({
    basePath,
    onApplyButtonClick,
    commentPermalink: fixWithCopilotProps?.commentPermalink,
  })

  if (taskUrl) {
    return <ViewAgentSessionButton taskUrl={taskUrl} />
  }

  return (
    <div className="d-flex gap-2 flex-items-center flex-justify-end flex-wrap">
      {fixWithCopilotProps ? (
        <SplitButton
          ref={applyButtonRef}
          items={RAILS_SPLIT_ITEMS}
          selectedValue={actionMode}
          onSelectionChange={setActionMode}
          onAction={handleMainButtonClick}
          variant="primary"
          size="small"
          dropdownAriaLabel="Change suggestion action"
        />
      ) : (
        <Button
          data-testid="commit-suggestion-button"
          size="small"
          variant="primary"
          ref={applyButtonRef}
          onClick={onApplyButtonClick}
        >
          Commit suggestion
        </Button>
      )}
      {showBatchButton && (
        <Tooltip text="Batching suggestions is only available in the Files changed tab">
          <Button data-testid="add-suggestion-to-batch-button" size="small" inactive>
            Add suggestion to batch
          </Button>
        </Tooltip>
      )}
      {isFixWithCopilotOpen && fixWithCopilotProps && (
        <CopilotFixDialog
          title="Fix with Copilot"
          onClose={() => setIsFixWithCopilotOpen(false)}
          onSubmit={handleFixSubmit}
          modelsUrl={fixWithCopilotProps.modelsUrl}
          closeOnSubmit={false}
        />
      )}
    </div>
  )
}

function ReactExperienceButtons({
  commentDatabaseId,
  applyButtonRef,
  onApplyButtonClick,
  suggestion,
  fixWithCopilotProps,
  basePath,
}: {
  commentDatabaseId: number
  applyButtonRef: React.RefObject<HTMLButtonElement | null>
  onApplyButtonClick: () => void
  suggestion: Suggestion | undefined
  fixWithCopilotProps?: AutomatedReviewSuggestionProps['fixWithCopilotProps']
  basePath: string
}) {
  const showApplyButton = !useIsSuggestionBatchPending()
  const showBatchButton = commentDatabaseId != null
  const {
    actionMode,
    setActionMode,
    taskUrl,
    isFixWithCopilotOpen,
    setIsFixWithCopilotOpen,
    handleMainButtonClick,
    handleFixSubmit,
  } = useFixWithCopilotActions({
    basePath,
    onApplyButtonClick,
    commentPermalink: fixWithCopilotProps?.commentPermalink,
  })

  const suggestionId = useMemo(() => ({commentDatabaseId, suggestionIndex: undefined}), [commentDatabaseId])
  const isPendingInBatch = useSuggestionInBatch(suggestionId)

  const suggestionBatch = useSuggestionBatch()
  const pathLineRanges = useMemo(
    () => getLineRangesFromDiffEntries(suggestion?.diffEntries || []),
    [suggestion?.diffEntries],
  )
  const {isValid: canAddToBatch, reason} = useMemo(
    () => validateSuggestedChangeCanBeAddedToBatch({pathLineRanges}, suggestionBatch.activeBatch),
    [pathLineRanges, suggestionBatch.activeBatch],
  )

  const canAddToOrRemoveFromBatch = isPendingInBatch || canAddToBatch

  if (taskUrl) {
    return <ViewAgentSessionButton taskUrl={taskUrl} />
  }

  return (
    <>
      {showBatchButton && !canAddToOrRemoveFromBatch && (
        <CodeSuggestionUnavailable
          reason={reason ?? 'Unable to commit due to other pending changes affecting this line.'}
          className="m-0"
        />
      )}
      <div className="d-flex gap-2 flex-items-center flex-justify-end flex-wrap">
        {showApplyButton &&
          (fixWithCopilotProps ? (
            <SplitButton
              ref={applyButtonRef}
              items={REACT_SPLIT_ITEMS}
              selectedValue={actionMode}
              onSelectionChange={setActionMode}
              onAction={handleMainButtonClick}
              dropdownAriaLabel="Change suggestion action"
            />
          ) : (
            <Button
              data-testid="commit-suggestion-button"
              variant="default"
              ref={applyButtonRef}
              onClick={onApplyButtonClick}
            >
              Apply suggestion
            </Button>
          ))}
        {showBatchButton && canAddToOrRemoveFromBatch && (
          <BatchButton commentDatabaseId={commentDatabaseId} pathLineRanges={pathLineRanges} />
        )}
      </div>
      {isFixWithCopilotOpen && fixWithCopilotProps && (
        <CopilotFixDialog
          title="Fix with Copilot"
          onClose={() => setIsFixWithCopilotOpen(false)}
          onSubmit={handleFixSubmit}
          modelsUrl={fixWithCopilotProps.modelsUrl}
          closeOnSubmit={false}
        />
      )}
    </>
  )
}

function BatchButton({
  commentDatabaseId,
  pathLineRanges,
}: {
  commentDatabaseId: number
  pathLineRanges: Array<{path: string; lineRange: SuggestedChangeLineRange}>
}) {
  const suggestionId = useMemo(() => ({commentDatabaseId, suggestionIndex: undefined}), [commentDatabaseId])
  const isPendingInBatch = useSuggestionInBatch(suggestionId)

  const suggestionBatch = useSuggestionBatch()
  const {addSuggestionToBatch, removeSuggestionFromBatch} = useSuggestionBatchActions()

  const pullRequestBasePath = usePullRequestBasePath()
  const comparisonInfo = useComparisonInfo()

  const batchedSuggestion = useMemo(
    () => ({...suggestionId, pathLineRanges, commentKind: 'ccr' as const}),
    [suggestionId, pathLineRanges],
  )

  const {sendAnalyticsEvent} = useAnalytics()
  const surface = useActiveRoute()

  const handleAddToBatch = useCallback(() => {
    addSuggestionToBatch(batchedSuggestion)
    saveActiveBatchToStorage(pullRequestBasePath, comparisonInfo.fullDiff.headOid, [
      ...suggestionBatch.activeBatch,
      batchedSuggestion,
    ])
    sendAnalyticsEvent('comments.add_suggested_change_to_batch', 'ADD_SUGGESTED_CHANGE_TO_BATCH_BUTTON', {
      commentKind: 'ccr',
      surface,
    })
  }, [
    addSuggestionToBatch,
    batchedSuggestion,
    suggestionBatch,
    pullRequestBasePath,
    comparisonInfo,
    sendAnalyticsEvent,
    surface,
  ])

  const handleRemoveFromBatch = useCallback(() => {
    removeSuggestionFromBatch(suggestionId)
    saveActiveBatchToStorage(
      pullRequestBasePath,
      comparisonInfo.fullDiff.headOid,
      suggestionBatch.activeBatch.filter(item => item.commentDatabaseId !== commentDatabaseId),
    )
    sendAnalyticsEvent('comments.remove_suggested_change_from_batch', 'REMOVE_SUGGESTED_CHANGE_FROM_BATCH_BUTTON', {
      commentKind: 'ccr',
      surface,
    })
  }, [
    removeSuggestionFromBatch,
    suggestionId,
    commentDatabaseId,
    suggestionBatch,
    pullRequestBasePath,
    comparisonInfo,
    sendAnalyticsEvent,
    surface,
  ])

  if (isPendingInBatch) {
    return (
      <div className="d-flex flex-items-center flex-justify-between flex-1">
        <Label variant="attention">Pending in batch</Label>
        <Button data-testid="remove-suggestion-from-batch-button" variant="danger" onClick={handleRemoveFromBatch}>
          Remove from batch
        </Button>
      </div>
    )
  }

  return (
    <Button data-testid="add-suggestion-to-batch-button" variant="primary" onClick={handleAddToBatch}>
      Add suggestion to batch
    </Button>
  )
}

function useFixWithCopilotActions({
  basePath,
  onApplyButtonClick,
  commentPermalink,
}: {
  basePath: string
  onApplyButtonClick: () => void
  commentPermalink: string | undefined
}) {
  const [actionMode, setActionMode] = useState<SuggestionActionMode>('commit')
  const [isFixWithCopilotOpen, setIsFixWithCopilotOpen] = useState(false)
  const {taskUrl, submitFix} = useFixWithCopilot({basePath})

  const handleMainButtonClick = useCallback(() => {
    if (actionMode === 'commit') {
      onApplyButtonClick()
    } else {
      setIsFixWithCopilotOpen(true)
    }
  }, [actionMode, onApplyButtonClick])

  const handleFixSubmit = useCallback(
    async (options: {createPullRequest: boolean; instructions: string; model: string}) => {
      if (!commentPermalink) return
      await submitFix({
        ...options,
        reviewReferences: [commentPermalink],
        fixAll: false,
      })
    },
    [commentPermalink, submitFix],
  )

  return {
    actionMode,
    setActionMode,
    taskUrl,
    isFixWithCopilotOpen,
    setIsFixWithCopilotOpen,
    handleMainButtonClick,
    handleFixSubmit,
  }
}
